import React, { useState } from "react";
import {
  IconUserCheck,
  IconPlus,
  IconSearch,
  IconArrowBackUp,
  IconBuildingCommunity,
  IconCheck,
  IconFileText,
  IconAlertCircle,
  IconCalendar,
  IconFilter
} from "@tabler/icons-react";
import { useAssetTransfers, useAssets, transferAsset } from "../../controllers/assets.controller";
import toast from "react-hot-toast";

export default function AssetAssignmentsTab() {
  const [search, setSearch] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false) || false;
  const [isReturnModalOpen, setIsReturnModalOpen] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);

  const { data: transfers, error, isLoading, mutate } = useAssetTransfers({ transfer_type: "Assignment" });
  const { data: assets } = useAssets({ status: "Available" });

  const [formData, setFormData] = useState({
    asset_id: "",
    to_custodian: "",
    assigned_employee: "",
    to_department: "Housekeeping",
    to_location: "",
    transfer_date: new Date().toISOString().split("T")[0],
    issued_condition: "Good",
    reason: "Assigned for operational duties",
    handover_notes: ""
  });

  const [returnFormData, setReturnFormData] = useState({
    receiving_condition: "Good",
    handover_notes: "Returned to central store in good condition"
  });

  const assignmentList = Array.isArray(transfers)
    ? transfers.filter((t) => {
        if (!search) return true;
        const q = search.toLowerCase();
        return (
          t.asset_code?.toLowerCase().includes(q) ||
          t.asset_name?.toLowerCase().includes(q) ||
          t.assigned_employee?.toLowerCase().includes(q) ||
          t.to_custodian?.toLowerCase().includes(q) ||
          t.to_department?.toLowerCase().includes(q)
        );
      })
    : [];

  const handleOpenAssignModal = () => {
    setFormData({
      asset_id: assets?.[0]?.id || "",
      to_custodian: "",
      assigned_employee: "",
      to_department: "Housekeeping",
      to_location: "",
      transfer_date: new Date().toISOString().split("T")[0],
      issued_condition: "Good",
      reason: "Assigned for regular hotel duties",
      handover_notes: ""
    });
    setIsModalOpen(true);
  };

  const handleCreateAssignment = async (e) => {
    e.preventDefault();
    if (!formData.asset_id) {
      toast.error("Please select an asset to assign");
      return;
    }
    if (!formData.assigned_employee && !formData.to_custodian) {
      toast.error("Please provide the custodian or employee name");
      return;
    }

    try {
      await transferAsset({
        ...formData,
        transfer_type: "Assignment",
        status: "Completed"
      });
      toast.success("Asset assigned successfully");
      setIsModalOpen(false);
      mutate();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to assign asset");
    }
  };

  const handleOpenReturnModal = (assignment) => {
    setSelectedAssignment(assignment);
    setReturnFormData({
      receiving_condition: "Good",
      handover_notes: "Asset returned and verified"
    });
    setIsReturnModalOpen(true);
  };

  const handleProcessReturn = async (e) => {
    e.preventDefault();
    if (!selectedAssignment) return;

    try {
      await transferAsset({
        asset_id: selectedAssignment.asset_id,
        transfer_type: "Return",
        from_custodian: selectedAssignment.to_custodian || selectedAssignment.assigned_employee,
        to_custodian: "Central Store Manager",
        from_department: selectedAssignment.to_department,
        to_department: "Inventory / Store",
        to_location: "Main Store Room",
        transfer_date: new Date().toISOString().split("T")[0],
        receiving_condition: returnFormData.receiving_condition,
        reason: "Operational return to stock",
        handover_notes: returnFormData.handover_notes,
        status: "Completed"
      });
      toast.success("Asset returned to store successfully");
      setIsReturnModalOpen(false);
      setSelectedAssignment(null);
      mutate();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to return asset");
    }
  };

  return (
    <div className="space-y-6">
      {/* Top Header / Action Toolbar */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white dark:bg-gray-800 p-5 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm">
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <IconUserCheck className="w-6 h-6 text-restro-green" />
            Asset Custody & Assignments
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Issue hotel equipment to staff, rooms, and departments with condition sign-off and return tracking.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <IconSearch className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search custodian, employee, asset..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 pr-4 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-restro-green w-64 text-gray-800 dark:text-gray-200"
            />
          </div>
          <button
            onClick={handleOpenAssignModal}
            className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-restro-green hover:bg-emerald-600 rounded-xl shadow-sm transition"
          >
            <IconPlus className="w-4 h-4" />
            Issue / Assign Asset
          </button>
        </div>
      </div>

      {/* Assignment Table */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm text-gray-600 dark:text-gray-300">
            <thead className="bg-gray-50 dark:bg-gray-700/50 text-gray-700 dark:text-gray-200 uppercase text-xs font-semibold">
              <tr>
                <th className="px-6 py-4">Asset Details</th>
                <th className="px-6 py-4">Assigned To / Custodian</th>
                <th className="px-6 py-4">Department & Location</th>
                <th className="px-6 py-4">Issue Date</th>
                <th className="px-6 py-4">Condition</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {isLoading ? (
                <tr>
                  <td colSpan="7" className="text-center py-10 text-gray-400">
                    Loading assignments...
                  </td>
                </tr>
              ) : assignmentList.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center py-12 text-gray-400">
                    <IconUserCheck className="w-12 h-12 mx-auto mb-2 opacity-30" />
                    No asset assignments recorded yet. Click "Issue / Assign Asset" to record the first assignment.
                  </td>
                </tr>
              ) : (
                assignmentList.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50/50 dark:hover:bg-gray-750 transition">
                    <td className="px-6 py-4">
                      <div className="font-semibold text-gray-900 dark:text-white">
                        {item.asset_name || "Asset #" + item.asset_id}
                      </div>
                      <div className="text-xs font-mono text-gray-400">
                        {item.asset_code || `AST-${item.asset_id}`}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900 dark:text-white">
                        {item.assigned_employee || item.to_custodian || "General Staff"}
                      </div>
                      {item.to_custodian && item.assigned_employee && (
                        <div className="text-xs text-gray-400">Custodian: {item.to_custodian}</div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1.5 text-gray-800 dark:text-gray-200">
                        <IconBuildingCommunity className="w-3.5 h-3.5 text-gray-400" />
                        {item.to_department || "General"}
                      </div>
                      <div className="text-xs text-gray-400 mt-0.5">
                        {item.to_location || "On-site"}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-xs">
                      {item.transfer_date ? new Date(item.transfer_date).toLocaleDateString() : "-"}
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800">
                        {item.issued_condition || "Good"}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400 border border-blue-200 dark:border-blue-800">
                        <IconCheck className="w-3 h-3" />
                        {item.status || "Active"}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => handleOpenReturnModal(item)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-200 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-lg transition"
                      >
                        <IconArrowBackUp className="w-3.5 h-3.5" />
                        Return to Store
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal: Issue/Assign Asset */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 overflow-y-auto">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-xl overflow-hidden border border-gray-100 dark:border-gray-700 my-8">
            <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <IconUserCheck className="w-5 h-5 text-restro-green" />
                Assign Asset to Custodian / Room
              </h3>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleCreateAssignment} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Select Available Asset *
                </label>
                <select
                  value={formData.asset_id}
                  onChange={(e) => setFormData({ ...formData, asset_id: e.target.value })}
                  className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-restro-green text-gray-800 dark:text-gray-200"
                  required
                >
                  <option value="">-- Choose an available asset --</option>
                  {assets?.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.asset_code} - {a.asset_name} ({a.category_name || "General"})
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    Assigned Staff / Employee *
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Ramesh Thapa"
                    value={formData.assigned_employee}
                    onChange={(e) => setFormData({ ...formData, assigned_employee: e.target.value })}
                    className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-restro-green text-gray-800 dark:text-gray-200"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    Custodian / Head of Department
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Executive Housekeeper"
                    value={formData.to_custodian}
                    onChange={(e) => setFormData({ ...formData, to_custodian: e.target.value })}
                    className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-restro-green text-gray-800 dark:text-gray-200"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    Department *
                  </label>
                  <select
                    value={formData.to_department}
                    onChange={(e) => setFormData({ ...formData, to_department: e.target.value })}
                    className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-restro-green text-gray-800 dark:text-gray-200"
                  >
                    <option value="Front Office">Front Office</option>
                    <option value="Housekeeping">Housekeeping</option>
                    <option value="Food & Beverage / Restaurant">Food & Beverage / Restaurant</option>
                    <option value="Kitchen / Culinary">Kitchen / Culinary</option>
                    <option value="Maintenance / Engineering">Maintenance / Engineering</option>
                    <option value="Accounts & Finance">Accounts & Finance</option>
                    <option value="Administration / Management">Administration / Management</option>
                    <option value="Security">Security</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    Room / Exact Location
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Room 204 or Reception Desk"
                    value={formData.to_location}
                    onChange={(e) => setFormData({ ...formData, to_location: e.target.value })}
                    className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-restro-green text-gray-800 dark:text-gray-200"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    Handover Date *
                  </label>
                  <input
                    type="date"
                    value={formData.transfer_date}
                    onChange={(e) => setFormData({ ...formData, transfer_date: e.target.value })}
                    className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-restro-green text-gray-800 dark:text-gray-200"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    Issued Condition
                  </label>
                  <select
                    value={formData.issued_condition}
                    onChange={(e) => setFormData({ ...formData, issued_condition: e.target.value })}
                    className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-restro-green text-gray-800 dark:text-gray-200"
                  >
                    <option value="New">Brand New</option>
                    <option value="Excellent">Excellent</option>
                    <option value="Good">Good / Working</option>
                    <option value="Fair">Fair</option>
                    <option value="Poor">Poor</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Reason & Purpose of Assignment
                </label>
                <input
                  type="text"
                  value={formData.reason}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                  placeholder="e.g. Issued for duty in guest executive suite"
                  className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-restro-green text-gray-800 dark:text-gray-200"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Handover Notes / Sign-off Acknowledgement
                </label>
                <textarea
                  rows="2"
                  value={formData.handover_notes}
                  onChange={(e) => setFormData({ ...formData, handover_notes: e.target.value })}
                  placeholder="e.g. Employee acknowledged safe receipt and operational testing"
                  className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-restro-green text-gray-800 dark:text-gray-200"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-xl transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 text-sm font-semibold text-white bg-restro-green hover:bg-emerald-600 rounded-xl shadow-sm transition"
                >
                  Confirm Assignment
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Return to Store */}
      {isReturnModalOpen && selectedAssignment && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 overflow-y-auto">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-md overflow-hidden border border-gray-100 dark:border-gray-700">
            <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <IconArrowBackUp className="w-5 h-5 text-restro-green" />
                Return Asset to Central Store
              </h3>
              <button
                onClick={() => setIsReturnModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleProcessReturn} className="p-6 space-y-4">
              <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded-xl border border-gray-200 dark:border-gray-700">
                <div className="font-semibold text-gray-900 dark:text-white">
                  {selectedAssignment.asset_name}
                </div>
                <div className="text-xs text-gray-400">
                  Tag: {selectedAssignment.asset_code} | Current: {selectedAssignment.assigned_employee || selectedAssignment.to_custodian}
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Receiving Condition *
                </label>
                <select
                  value={returnFormData.receiving_condition}
                  onChange={(e) => setReturnFormData({ ...returnFormData, receiving_condition: e.target.value })}
                  className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-restro-green text-gray-800 dark:text-gray-200"
                >
                  <option value="Good">Good / Working Normal</option>
                  <option value="Fair">Fair / Minor Wear</option>
                  <option value="Damaged">Damaged / Needs Repair</option>
                  <option value="Defective">Defective / Non-Functional</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Inspection & Handover Notes
                </label>
                <textarea
                  rows="3"
                  value={returnFormData.handover_notes}
                  onChange={(e) => setReturnFormData({ ...returnFormData, handover_notes: e.target.value })}
                  placeholder="Notes on asset condition upon return to central store..."
                  className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-restro-green text-gray-800 dark:text-gray-200"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => setIsReturnModalOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-xl transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 text-sm font-semibold text-white bg-restro-green hover:bg-emerald-600 rounded-xl shadow-sm transition"
                >
                  Acknowledge Return
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
